import React, { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { MoonSunToggle } from "./components/ui/moon-sun-toggle";
import ScrollToTop from "@/components/ScrollToTop";
import { useAnalytics } from "./hooks/useAnalytics";
import { CookieConsent } from "./components/CookieConsent";

const Index = lazy(() => import("./pages/Index"));
const Therapy = lazy(() => import("./pages/Therapy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Profile = lazy(() => import("./pages/Team/Profile"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Courses = lazy(() => import("./pages/Courses"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
  </div>
);

// Must live inside BrowserRouter to call useLocation via useAnalytics
const AppRoutes = () => {
  useAnalytics();
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/terapia" element={<Therapy />} />
        <Route path="/equipe/:slug" element={<Profile />} />
        <Route path="/contato" element={<Contact />} />
        <Route path="/sobre" element={<About />} />
        <Route path="/cursos" element={<Courses />} />
        <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
        <Route path="/feed" element={<Navigate to="/" replace />} />
        <Route path="/start" element={<Navigate to="/" replace />} />
        <Route path="/comecar" element={<Navigate to="/" replace />} />
        <Route path="/entrar" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/reset-password" element={<Navigate to="/" replace />} />
        <Route path="/confirm-email" element={<Navigate to="/" replace />} />
        <Route path="/post/:id" element={<Navigate to="/" replace />} />
        <Route path="/perfil" element={<Navigate to="/" replace />} />
        <Route path="/perfil/:username" element={<Navigate to="/" replace />} />
        <Route path="/analytics" element={<Navigate to="/" replace />} />
        <Route path="/como-funciona" element={<Navigate to="/#methodology" replace />} />
        <Route path="/quem-somos" element={<Navigate to="/#who-we-are" replace />} />
        <Route path="/recursos" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <HelmetProvider>
    <ThemeProvider defaultTheme="dark">
      <ErrorBoundary>
        <TooltipProvider>
          <Sonner />
          <CookieConsent />
          <div className="fixed bottom-4 left-2 z-30">
            <MoonSunToggle />
          </div>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <ScrollToTop />
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
